import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Wallet, Copy, Check, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentModal = ({ isOpen, onClose, plan, onSuccess }) => {
  const [step, setStep] = useState('select'); // select, payment, status
  const [selectedCurrency, setSelectedCurrency] = useState('btc');
  const [currencies, setCurrencies] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Popular currencies for quick selection
  const popularCurrencies = ['btc', 'eth', 'ltc', 'usdt', 'usdc', 'sol', 'bnb'];

  useEffect(() => {
    if (isOpen) {
      fetchCurrencies();
      setStep('select');
      setPaymentData(null);
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (plan && selectedCurrency) {
      fetchEstimate();
    }
  }, [selectedCurrency, plan]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/currencies`);
      setCurrencies(response.data.currencies || popularCurrencies);
    } catch (err) {
      console.error('Failed to fetch currencies:', err);
      setCurrencies(popularCurrencies);
    }
  };

  const fetchEstimate = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/estimate`, {
        params: {
          amount: plan.price,
          currency_to: selectedCurrency
        }
      });
      setEstimate(response.data);
    } catch (err) {
      console.error('Failed to fetch estimate:', err);
    }
  };

  const handleCreatePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/payments/create`, {
        plan_type: plan.type,
        account_size: plan.capital,
        price_amount: plan.price,
        pay_currency: selectedCurrency
      });

      // If we got an invoice URL, redirect to it immediately
      if (response.data.invoice_url) {
        window.location.href = response.data.invoice_url;
        return;
      }

      // Fallback: show payment data if no invoice URL (shouldn't happen)
      setPaymentData(response.data);
      setStep('payment');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!paymentData?.order_id) return;

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/payments/status/${paymentData.order_id}`);
      
      if (response.data.payment_status === 'finished' || response.data.payment_status === 'confirmed') {
        onSuccess?.(response.data);
        onClose();
      } else if (response.data.payment_status === 'failed' || response.data.payment_status === 'expired') {
        setError(`Payment ${response.data.payment_status}. Please try again.`);
      }
      
      setPaymentData(prev => ({
        ...prev,
        payment_status: response.data.payment_status,
        actually_paid: response.data.actually_paid
      }));
    } catch (err) {
      console.error('Failed to check status:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
      case 'confirmed':
        return 'var(--success)';
      case 'waiting':
      case 'confirming':
        return 'var(--warning)';
      case 'failed':
      case 'expired':
        return 'var(--error)';
      default:
        return 'var(--text-muted)';
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      {/* Backdrop */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div style={{
        position: 'relative',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: '24px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }}>
              {step === 'select' ? 'Complete Payment' : 'Send Payment'}
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)'
            }}>
              {plan?.type.toUpperCase()} Plan - ${plan?.capital.toLocaleString()} Account
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <AlertCircle size={20} color="var(--error)" />
              <p style={{ color: 'var(--error)', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          {step === 'select' && (
            <>
              {/* Price Display */}
              <div style={{
                background: 'var(--bg-primary)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Amount to Pay
                </p>
                <p style={{
                  fontSize: '36px',
                  fontWeight: 700,
                  color: 'var(--accent-gold)'
                }}>
                  ${plan?.price}
                </p>
                {estimate && estimate.estimated_amount && (
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    marginTop: '8px'
                  }}>
                    ≈ {Number(estimate.estimated_amount).toFixed(8)} {selectedCurrency.toUpperCase()}
                  </p>
                )}
              </div>

              {/* Currency Selection */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '12px'
                }}>
                  Select Payment Currency
                </label>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  {popularCurrencies.map(currency => (
                    <button
                      key={currency}
                      onClick={() => setSelectedCurrency(currency)}
                      style={{
                        padding: '12px',
                        background: selectedCurrency === currency 
                          ? 'rgba(212, 175, 55, 0.2)' 
                          : 'var(--bg-primary)',
                        border: selectedCurrency === currency 
                          ? '2px solid var(--accent-gold)' 
                          : '1px solid var(--border-subtle)',
                        borderRadius: '10px',
                        color: selectedCurrency === currency 
                          ? 'var(--accent-gold)' 
                          : 'var(--text-secondary)',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {currency}
                    </button>
                  ))}
                </div>

                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '10px',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {currencies.map(currency => (
                    <option key={currency} value={currency}>
                      {currency.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pay Button */}
              <Button
                onClick={handleCreatePayment}
                disabled={loading}
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid transparent',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Creating Payment...
                  </>
                ) : (
                  <>
                    <Wallet size={20} />
                    Pay with {selectedCurrency.toUpperCase()}
                  </>
                )}
              </Button>
            </>
          )}

          {step === 'payment' && paymentData && (
            <>
              {/* Payment Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: getStatusColor(paymentData.payment_status),
                  animation: paymentData.payment_status === 'waiting' ? 'pulse 2s infinite' : 'none'
                }} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: getStatusColor(paymentData.payment_status),
                  textTransform: 'uppercase'
                }}>
                  {paymentData.payment_status}
                </span>
              </div>

              {/* Amount to Send */}
              <div style={{
                background: 'var(--bg-primary)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Send Exactly
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  <p style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: 'var(--accent-gold)'
                  }}>
                    {paymentData.pay_amount} {paymentData.pay_currency?.toUpperCase()}
                  </p>
                  <button
                    onClick={() => copyToClipboard(String(paymentData.pay_amount))}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      color: copied ? 'var(--success)' : 'var(--text-muted)'
                    }}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {/* Payment Address */}
              <div style={{
                background: 'var(--bg-primary)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-muted)',
                  marginBottom: '12px'
                }}>
                  To This Address
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '10px',
                  padding: '12px'
                }}>
                  <code style={{
                    flex: 1,
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace'
                  }}>
                    {paymentData.pay_address}
                  </code>
                  <button
                    onClick={() => copyToClipboard(paymentData.pay_address)}
                    style={{
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      color: copied ? 'var(--success)' : 'var(--text-muted)',
                      flexShrink: 0
                    }}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div style={{
                background: 'rgba(14, 165, 233, 0.1)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6
                }}>
                  <strong style={{ color: 'var(--accent-blue)' }}>Important:</strong> Send the exact amount shown above. After sending, your payment will be confirmed within a few minutes. Do not close this window.
                </p>
              </div>

              {/* Check Status Button */}
              <Button
                onClick={checkPaymentStatus}
                disabled={checkingStatus}
                variant="outline"
                style={{
                  width: '100%',
                  padding: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: '1px solid var(--border-primary)',
                  background: 'transparent',
                  color: 'var(--text-primary)'
                }}
              >
                <RefreshCw size={18} className={checkingStatus ? 'animate-spin' : ''} />
                {checkingStatus ? 'Checking...' : 'Check Payment Status'}
              </Button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PaymentModal;
